import { useCallback, useEffect, useRef } from "react";

/**
 * useReveal — Adds `is-visible` to `.reveal` descendants when they enter the viewport.
 * Uses a callback ref so it works even when the wrapper mounts after an initial loading state.
 */
export const useReveal = <T extends HTMLElement = HTMLElement>() => {
  const nodeRef = useRef<T | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const setup = useCallback((root: T) => {
    const SELECTOR = ".reveal, .reveal-up, .reveal-left, .reveal-right, .reveal-zoom";
    // Reveal instantly, with no animation and no stagger delay.
    const showNow = (el: HTMLElement) => {
      el.classList.add("reveal-instant", "is-visible");
    };
    if (typeof IntersectionObserver === "undefined") {
      root.querySelectorAll<HTMLElement>(SELECTOR).forEach(showNow);
      return () => {};
    }
    const seen = new WeakSet<HTMLElement>();
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: isMobile ? 0.06 : 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    const observeNew = () => {
      root.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
        if (el.classList.contains("is-visible") || seen.has(el)) return;
        seen.add(el);
        // Anything already in or above the viewport is shown at once. Only
        // content still below the fold gets the entrance animation.
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) {
          showNow(el);
          return;
        }
        io.observe(el);
      });
    };
    observeNew();
    const mo = new MutationObserver(observeNew);
    mo.observe(root, { childList: true, subtree: true });
    // Failsafe: if the observer has not fired within 800ms, force everything
    // visible. Content must never be invisible while the reader is looking at it.
    const failsafe = window.setTimeout(() => {
      root.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
        if (!el.classList.contains("is-visible")) showNow(el);
      });
    }, 800);
    return () => {
      window.clearTimeout(failsafe);
      mo.disconnect();
      io.disconnect();
    };
  }, []);


  const ref = useCallback(
    (node: T | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      nodeRef.current = node;
      if (node) {
        cleanupRef.current = setup(node);
      }
    },
    [setup]
  );

  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  // Cast for compatibility with existing `ref={reveal}` usage on typed elements.
  return ref as unknown as React.MutableRefObject<T | null>;
};
